import { AxiosInstance, AxiosRequestConfig } from 'axios'

import { ApprovalProcessRequestorRepository } from './domain'

import {
  ApprovalProcessRequestorView,
  CreationDraftRequest,
  CreationRequest,
  ModificationDraftRequest,
  ModificationRequest,
  PartnerApprovalProcessListItem,
  PartnerServiceOfferDraft,
  PartnerServiceOfferDraftListItem,
} from '../../contracts/service-offer'
import { AxiosErrorHandler, PaginationCriteria, SupernovaPagedBody } from '../../foundation'

export class PartnerApiApprovalProcess implements ApprovalProcessRequestorRepository {
  private errorHandler: AxiosErrorHandler

  constructor(private axios: AxiosInstance) {
    this.errorHandler = new AxiosErrorHandler()
  }

  getById = async (id: number): Promise<ApprovalProcessRequestorView> => {
    const config: AxiosRequestConfig<{ approvalProcessId: number }> = {
      url: '/service-offer/query/partner/approval-process/process',
      method: 'post',
      data: { approvalProcessId: id },
    }

    try {
      const { data } = await this.axios.request<ApprovalProcessRequestorView>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getList = async (
    criteria: PaginationCriteria<{ approvalProcessId: number | null }>,
  ): Promise<SupernovaPagedBody<PartnerApprovalProcessListItem[]>> => {
    const config: AxiosRequestConfig<PaginationCriteria<{ approvalProcessId: number | null }>> = {
      url: '/service-offer/query/partner/approval-process-list/process',
      method: 'post',
      data: criteria,
    }

    try {
      const { data } = await this.axios.request<SupernovaPagedBody<PartnerApprovalProcessListItem[]>>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  postCreationRequest = async (command: CreationRequest): Promise<{ id: number }> => {
    const config: AxiosRequestConfig<CreationRequest> = {
      url: '/service-offer/command/partner/creation-request/execute',
      method: 'post',
      data: command,
    }

    try {
      const { data } = await this.axios.request<{ id: number }>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  postModificationRequest = async (command: ModificationRequest): Promise<{ id: number }> => {
    const config: AxiosRequestConfig<ModificationRequest> = {
      url: '/service-offer/command/partner/modification-request/execute',
      method: 'post',
      data: command,
    }

    try {
      const { data } = await this.axios.request<{ id: number }>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getCreationDraftList = async (): Promise<PartnerServiceOfferDraftListItem[]> => {
    const config: AxiosRequestConfig = {
      url: '/service-offer/query/partner/creation-approval-process-drafts/process',
      method: 'post',
    }

    try {
      const { data } = await this.axios.request<{ contents: PartnerServiceOfferDraftListItem[] }>(config)
      return data.contents
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getCreationDraft = async (draftId: string): Promise<PartnerServiceOfferDraft> => {
    const config: AxiosRequestConfig<{ id: string }> = {
      url: '/service-offer/query/partner/creation-approval-process-draft/process',
      method: 'post',
      data: { id: draftId },
    }

    try {
      const { data } = await this.axios.request<PartnerServiceOfferDraft>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  getModificationDraft = async (serviceOfferId: number): Promise<PartnerServiceOfferDraft> => {
    const config: AxiosRequestConfig<{ serviceOfferId: number }> = {
      url: '/service-offer/query/partner/modification-approval-process-draft/process',
      method: 'post',
      data: { serviceOfferId },
    }

    try {
      const { data } = await this.axios.request<PartnerServiceOfferDraft>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  saveCreationDraft = async (command: CreationDraftRequest): Promise<{ id: string }> => {
    const config: AxiosRequestConfig<CreationDraftRequest> = {
      url: '/service-offer/command/partner/save-creation-approval-process-draft/execute',
      method: 'post',
      data: { ...command },
    }

    try {
      const { data } = await this.axios.request<{ id: string }>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  saveModificationDraft = async (command: ModificationDraftRequest): Promise<{ id: string }> => {
    const config: AxiosRequestConfig<ModificationDraftRequest> = {
      url: '/service-offer/command/partner/save-modification-approval-process-draft/execute',
      method: 'post',
      data: { ...command },
    }

    try {
      const { data } = await this.axios.request<{ id: string }>(config)
      return data
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }

  deleteDraft = async (draftIds: string[]): Promise<void> => {
    const config: AxiosRequestConfig<{ ids: string[] }> = {
      url: '/service-offer/command/partner/delete-approval-process-drafts/execute',
      method: 'post',
      data: { ids: draftIds },
    }

    try {
      await this.axios.request<void>(config)
    } catch (error) {
      return this.errorHandler.handle(error)
    }
  }
}
